/**
 *
 * IBM Confidential
 *
 * (C) Copyright IBM Corp. 2021, 2024
 *
 * The source code for this program is not published or otherwise
 * divested of its trade secrets, irrespective of what has been
 * deposited with the U. S. Copyright Office
 *
 * US Government Users Restricted Rights - Use, duplication or
 * disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 *
 **/

/**
 * IMPORTANT NOTE FOR FUTURE TRAVELERS
 *
 * This is the file parser for the uploaded OpenAPI spec and what this file parses is ultimately saved.
 *
 * Note that if you make changes here, and the tests fail, that means that anyone that uploaded an OpenAPI spec before
 * you made your change will not have the new functionality you have added.
 *
 * Beware those who come here.
 */

import SwaggerParser from '@apidevtools/swagger-parser';
import _ from 'lodash';
import { OpenAPIV3 } from 'openapi-types';

import {
  PathedSchemaObject,
  TransformedOpenAPISpec,
  TransformedPath,
  TransformedRequestProperty,
  TransformedResponseProperty
} from '../../../ts/catalog';
import {
  ARRAY,
  MEDIA_EVENT_STREAM,
  MEDIA_APPLICATION_JSON
} from '../components/catalog-openapi/constants';
import { RAN_SUCCESSFULLY, SUCCESS_PATH } from './constants';
// If you do need to update all the test results, use this.
// import { printUpdatedTestResults } from './helpers';

const REQUEST_TEMPLATE: TransformedRequestProperty = {
  title: '',
  property: '',
  path: '',
  property_type: '',
  description: '',
  data_type: '',
  example: '',
  required: false,
  default: ''
} as TransformedRequestProperty;

/**
 * Function to read the spec using SwaggerParser
 *
 * @param {Object} spec JSON Object representing OpenAPI 3.0 Spec
 * @returns {object} parsed Swagger doc into a JSON object we can transform
 */
export const parseSpec = async (
  spec: string | OpenAPIV3.Document
): Promise<OpenAPIV3.Document> => {
  // See https://github.com/APIDevTools/swagger-parser/blob/main/docs/swagger-parser.md#dereferenceapi-options-callback
  // We check for errors on upload so we don't do it again here and assume it is OK. This is why
  // there is no "options" object passed to dereference here.
  const parsed = (await SwaggerParser.dereference(spec)) as OpenAPIV3.Document;
  return parsed;
};

/**
 * Utility function to retrieve the Paths/Endpoints from the spec for iterating over
 *
 * @param {*} parsedSpec
 * @returns
 */
const paths = (parsedSpec: OpenAPIV3.Document): string[] =>
  Object.keys(parsedSpec?.paths);

/**
 * Add the url path to the property object. OpenAPI spec defines the path as the key to the property, but we need it as
 * a value since we flatten to an array instead of a nested object.
 *
 * @param {string} path The path that was the key in the parent object
 * @param {object} property property object
 * @returns {object}
 */
const propertyToPath = (
  path: string,
  property: OpenAPIV3.SchemaObject
): PathedSchemaObject =>
  ({
    ...property,
    path
  } as PathedSchemaObject);
/**
 *  Recursively align properties to a path
 *  ex. a nested object needs to be walked to get the path of 'grandparent.parent.child'
 *
 * This is also where is a property is required or not is added if coming from the required array value.
 *
 * @param {string} parent immediate parent of this property
 * @param {object} properties object of properties for the current path
 * @param {array} required An array of properties that are required.
 * @returns {array} normalized properties
 */
const propertiesToPath = (
  parent: string | null,
  properties: {
    [property: string]: OpenAPIV3.ReferenceObject | OpenAPIV3.SchemaObject;
  },
  required: string[] | boolean
): PathedSchemaObject[] => {
  const normalizedProps = [];
  Object.keys(properties).forEach(key => {
    // If there is a parent, it means its an object and will have properties to parse.
    const completePath = parent ? `${parent}.${key}` : key;
    const property = properties[key] as OpenAPIV3.SchemaObject;
    if (property?.properties) {
      propertiesToPath(
        completePath,
        property?.properties,
        property?.required
      ).forEach(item => normalizedProps.push(item));
      // OK this is messy... once we are done the forEach, if this whole object is required we set it to the boolean.
      // This is the same key that has the array in the OpenAPI spec. We are going to have to rework this to not be
      // stepping on top of itself.
      if (required === true || (required as string[])?.includes(key)) {
        property.required = true as any; // openapi-types does not have `required` as a boolean
      }
    } else {
      // If this property is required by the required array... make it so.
      if (required === true || (required as string[])?.includes(key)) {
        property.required = true as any; // openapi-types does not have `required` as a boolean
      }
      normalizedProps.push(propertyToPath(completePath, property));
    }
  });

  return normalizedProps;
};

/**
 * Properties may contain a schema, we need to convert the Schema object
 * to a list of properties with paths.
 *
 * @param {object} schema
 * @returns {array}
 */
const schemaToPath = (
  schema: OpenAPIV3.SchemaObject
): PathedSchemaObject[] | OpenAPIV3.SchemaObject[] => {
  if ((schema as OpenAPIV3.ArraySchemaObject)?.type === ARRAY) {
    return [schema];
  } else if (schema?.properties) {
    return propertiesToPath(null, schema?.properties, schema?.required);
  } else {
    return [];
  }
};

/**
 * Convert a requestBody to a list of properties
 *
 * @param {object} requestBody RequestBody from spec
 * @returns {array}
 */
export const transformRequestBody = (
  requestBody: OpenAPIV3.RequestBodyObject
): TransformedRequestProperty[] => {
  if (requestBody) {
    // We rely on the server to transform from YAML to JSON.
    return schemaToPath(
      requestBody?.content[MEDIA_APPLICATION_JSON]
        ?.schema as OpenAPIV3.SchemaObject
    ).map(({ type, path, ...rest }) => {
      return {
        ...REQUEST_TEMPLATE,
        data_type: type,
        title: path,
        path: path,
        property: path ? path.split('.').pop() : undefined,
        property_type: 'body',
        ...rest
      } as TransformedRequestProperty;
    });
  } else {
    return [];
  }
};

/**
 * Parameters are defined for the request and these are mapped separately
 *
 * @param {array} parameters
 * @returns {array}
 */
export const transformParameters = (
  parameters: OpenAPIV3.ParameterObject[]
): TransformedRequestProperty[] => {
  if (parameters) {
    return parameters.map(parameter => {
      const { schema, name, ...rest } = parameter;
      const objSchema = schema as OpenAPIV3.SchemaObject;
      const arraySchema = schema as OpenAPIV3.ArraySchemaObject;
      const dataType = objSchema?.type ?? 'string';
      const defaultValue =
        dataType !== 'array'
          ? objSchema?.default ?? ''
          : arraySchema?.default ?? [];
      return {
        ...REQUEST_TEMPLATE,
        data_type: dataType,
        default: defaultValue,
        title: name,
        path: name,
        property: name,
        property_type: rest?.in,
        ...rest
      } as TransformedRequestProperty;
    });
  } else {
    return [];
  }
};

/**
 * Parses over the "content['application/json']" or "content['text/event-stream']" portion of the response and returns a parsed value.
 */
const transformBodyResponses = (
  responses: OpenAPIV3.ResponsesObject,
  status: string
): TransformedResponseProperty[] => {
  const schemaApplicationJson = (responses[status] as OpenAPIV3.ResponseObject)
    ?.content
    ? (responses[status] as OpenAPIV3.ResponseObject)?.content[
        MEDIA_APPLICATION_JSON
      ]?.schema
    : undefined;

  const schemaMediaStream = (responses[status] as OpenAPIV3.ResponseObject)
    ?.content
    ? (responses[status] as OpenAPIV3.ResponseObject)?.content[
        MEDIA_EVENT_STREAM
      ]?.schema
    : undefined;

  const schema = schemaApplicationJson || schemaMediaStream;

  if (schema) {
    return schemaToPath(schema as OpenAPIV3.SchemaObject).map(
      ({ type, path, description, ...rest }) =>
        ({
          id: `status.${status}.${path ? `body.${path}` : 'body'}`,
          title: path ? `body.${path}` : 'body',
          enabled: true,
          status: status,
          description: description,
          data_type: type,
          path: path ? `body.${path}` : 'body',
          property: path ? path.split('.').pop() : undefined,
          property_type: 'body',
          ...rest
        } as TransformedResponseProperty)
    );
  }
  return [];
};

/**
 * Parses over the "headers" portion of the response and returns a parsed value. Use of these values is not
 * currently supported.
 */
const transformHeaderResponses = (
  responses: OpenAPIV3.ResponsesObject,
  status: string
): TransformedResponseProperty[] => {
  const headers = (responses[status] as OpenAPIV3.ResponseObject)?.headers;
  if (headers) {
    const headerArray = [];
    Object.keys(headers).forEach(header => {
      const path = header.toLowerCase();
      const schema = (headers[header] as OpenAPIV3.HeaderObject)
        .schema as OpenAPIV3.SchemaObject;
      headerArray.push({
        id: `status.${status}.headers.${path}`,
        title: `headers.${path}`,
        enabled: false,
        status: status,
        description: schema.description,
        data_type: schema.type,
        path: `headers.${path}`,
        property: path ? path.split('.').pop() : undefined,
        property_type: 'headers',
        example: schema.example,
        ...headers[header]
      } as TransformedResponseProperty);
    });
    return headerArray;
  }
  return [];
};

/**
 * Parses over the "links" portion of the response and returns a parsed value. Use of these values is not
 * currently supported.
 */
const transformLinksResponses = (
  responses: OpenAPIV3.ResponsesObject,
  status: string
): TransformedResponseProperty[] => {
  const links = (responses[status] as OpenAPIV3.ResponseObject)?.links;
  if (links) {
    const linksArray: TransformedResponseProperty[] = [];
    Object.keys(links).forEach(link => {
      const path = link.toLowerCase();
      const { parameters, requestBody, ...rest } = links[
        link
      ] as OpenAPIV3.LinkObject;
      linksArray.push({
        id: `status.${status}.links.${path}`,
        title: `links.${path}`,
        enabled: false,
        status: status,
        path: `links.${path}`,
        property: path ? path.split('.').pop() : undefined,
        property_type: 'links',
        request_properties: [
          ...transformParameters(parameters as OpenAPIV3.ParameterObject[]),
          ...transformRequestBody(requestBody as OpenAPIV3.RequestBodyObject)
        ],
        ...rest
      } as TransformedResponseProperty);
    });
    return linksArray;
  }
  return [];
};

/**
 * Responses come in as possible status (default, 200, 400, etc ). The method will combine the responses from headers,
 * content (body) and links into one array. The property_type will let us know where the response is located.
 *
 * Only body/content is currently supported by in UI and backend.
 *
 * @param {object} responses
 * @returns {array}
 */
const transformResponses = (
  responses: OpenAPIV3.ResponsesObject
): TransformedResponseProperty[] => {
  let responseMapping = [];
  if (responses) {
    Object.keys(responses).forEach(status => {
      let individualResponseMapping = [];
      individualResponseMapping = individualResponseMapping.concat(
        transformBodyResponses(responses, status)
      );
      individualResponseMapping = individualResponseMapping.concat(
        transformHeaderResponses(responses, status)
      );
      individualResponseMapping = individualResponseMapping.concat(
        transformLinksResponses(responses, status)
      );
      if (individualResponseMapping.length > 0) {
        responseMapping = responseMapping.concat(individualResponseMapping);
      } else {
        responseMapping = responseMapping.concat({
          status: status,
          description: (responses[status] as OpenAPIV3.ResponseObject)
            ?.description,
          ...responses[status]
        });
      }
    });
  }
  return responseMapping;
};

/**
 * Transform a Path into our preferred format
 *
 * @param {string} path
 * @param {object} pathObject
 * @returns {object}
 */
const transformPath = (
  path: string,
  pathObject: OpenAPIV3.PathItemObject
): TransformedPath[] => {
  return Object.keys(pathObject)
    .map(method => {
      if (typeof pathObject[method] !== 'object') {
        return undefined;
      }
      const { parameters, requestBody, responses, operationId, ...rest } =
        pathObject[method];

      const transformedParameters = transformParameters(parameters);
      const transformedRequestBody = transformRequestBody(requestBody);
      const defaultTitle = path.concat(' ').concat(method.toUpperCase());

      return {
        id: operationId,
        path: path,
        method: method ? method.toUpperCase() : 'none',
        ...rest,
        request_properties: [
          ...transformedParameters,
          ...transformedRequestBody
        ],
        response_properties: [
          {
            id: SUCCESS_PATH,
            data_type: 'boolean',
            path: SUCCESS_PATH,
            enabled: true,
            property_type: SUCCESS_PATH,
            title: RAN_SUCCESSFULLY
          },
          ...transformResponses(responses)
        ],
        // for now we only have 1 content type per operation
        content: responses?.[200]?.content
          ? Object.keys(responses?.[200]?.content)?.[0]
          : undefined,
        enabled: true,
        title: rest.summary || rest.description || defaultTitle
      };
    })
    .filter(item => Boolean(item));
};

/**
 * Main function to transform an OpenAPI spec into our preferred format
 *
 * @param {object} openAPISpec parsed spec from swagger-parser
 * @returns {object} transformed spec
 */
export const transformOpenAPISpec = async (
  openAPISpec: string | OpenAPIV3.Document
): Promise<TransformedOpenAPISpec> => {
  const parsed = await parseSpec(_.cloneDeep(openAPISpec));

  // Each url has the methods (GET, POST, etc) in an object underneath.
  // Here we are going to flatten that and add `method: type` as a property on each item in the flattened object.
  const endpoints = paths(parsed)?.reduce<TransformedPath[]>(
    (total, path: string) => {
      const pathEndpoints = transformPath(path, parsed.paths[path]);
      return total.concat(pathEndpoints);
    },
    []
  );

  return {
    info: { ...parsed.info },
    endpoints: endpoints
  };
};

/**
 * Returns raw results from Swagger-parser.  Useful for seeing how to reference the data.
 *
 * @param {Object} openAPISpec JSON object representing an openAPISpec
 * @returns {Promise} object containing transformed/understood OpenAPISpec
 */
export const parseOpenAPISpec = async (
  openAPISpec: string | OpenAPIV3.Document
): Promise<OpenAPIV3.Document> => {
  return parseSpec(_.cloneDeep(openAPISpec));
};
